import httpClient from '../../utils/httpClient';
import { API_V3_URL } from '../../configs/env.conf';

const HOMEWORK_URL = API_V3_URL;

export interface Question {
  question: string;
  options: string[];
  answer: number;
  explanation: string;
  skill: string;
}

export interface HomeworkSection {
  title: string;
  type: string;
  questions: Question[];
}

export interface HomeworkContent {
  topic: string;
  level: string;
  sections: HomeworkSection[];
}

export interface SubmissionData {
  homeworkId: string | number;
  studentId: string | number;
  studentAnswers: any;
  score: number;
  gradingResult: {
    totalCorrect: number;
    totalQuestions: number;
    details: Array<{
      questionIndex: number;
      isCorrect: boolean;
    }>;
  };
  startedAt: string;
  source?: string; // Track submission source: 'web' or 'app'
}

export interface HomeworkSaveData {
  unitId: number;
  studentId: number;
  teacherId: number;
  status: string;
  content: HomeworkContent;
  configSnapshot?: {
    topic: string;
    level: string;
    source: string;
  };
  source?: string; // Track where the assignment message comes from: 'web' or 'app'
}

export interface GenerateHomeworkParams {
  file: File;
  ageGroup: string;
  numQuestions: number;
}

export interface GenerateHomeworkFromVideoParams {
  file: File;
}

/**
 * Fetch homework details by ID
 */
export const getHomework = (homeworkId: string) => {
  return httpClient.get(`${HOMEWORK_URL}/homework/${homeworkId}`);
};

/**
 * Submit homework results
 */
export const submitHomework = (data: SubmissionData) => {
  return httpClient.post(`${HOMEWORK_URL}/homework/submit`, data);
};

/**
 * Generate homework from image/PDF
 */
export const generateHomeworkFromImage = (params: GenerateHomeworkParams) => {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('ageGroup', params.ageGroup);
  formData.append('numQuestions', params.numQuestions.toString());

  return httpClient.post(
    `${HOMEWORK_URL}/homework/generate-from-image`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

/**
 * Generate homework from video
 */
export const generateHomeworkFromVideo = (
  params: GenerateHomeworkFromVideoParams,
) => {
  const formData = new FormData();
  formData.append('file', params.file);

  return httpClient.post(
    `${HOMEWORK_URL}/homework/generate-from-video`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 minutes for video processing
    },
  );
};

/**
 * Save homework after editing
 */
export const saveHomework = (data: HomeworkSaveData) => {
  return httpClient.post(`${HOMEWORK_URL}/homework/save`, data);
};
